import { useCallback, useEffect, useSyncExternalStore } from "react";

type UseLocalStorage<T> = [
  value: T,
  update: (newValue: T) => void,
  remove: () => void,
];

function safeLocalStorageOperation<T>(operation: () => T): T | null {
  try {
    if (localStorage === null || localStorage === undefined) {
      return null;
    }

    return operation();
  } catch (error) {
    if (error instanceof Error && error.name === "SecurityError") {
      return null;
    }
    throw error;
  }
}

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): UseLocalStorage<T> {
  const getSnapshot = useCallback(() => getLocalStorageItem(key), [key]);

  const store = useSyncExternalStore(subscribe, getSnapshot);

  const update = useCallback(
    (newValue: T) => {
      if (newValue === undefined || newValue === null) {
        removeLocalStorageItem(key);
      } else {
        setLocalStorageItem(key, newValue);
      }
    },
    [key],
  );

  const remove = useCallback(() => {
    removeLocalStorageItem(key);
  }, [key]);

  useEffect(() => {
    if (getLocalStorageItem(key) === null && initialValue !== undefined) {
      setLocalStorageItem(key, initialValue);
    }
  }, [key, initialValue]);

  const value = store ? (JSON.parse(store) as T) : initialValue;

  return [value, update, remove];
}

function subscribe(onStoreChange: () => void) {
  window.addEventListener("storage", onStoreChange);

  return () => {
    window.removeEventListener("storage", onStoreChange);
  };
}

function getLocalStorageItem(key: string): string | null {
  return safeLocalStorageOperation<string | null>(() =>
    localStorage.getItem(key),
  );
}

function setLocalStorageItem<T>(key: string, value: T): void {
  safeLocalStorageOperation(() => {
    const stringifiedValue = JSON.stringify(value);
    localStorage.setItem(key, stringifiedValue);
    dispatchStorageEvent(key, stringifiedValue);
  });
}

function removeLocalStorageItem(key: string): void {
  safeLocalStorageOperation(() => {
    localStorage.removeItem(key);
    dispatchStorageEvent(key, null);
  });
}

function dispatchStorageEvent(key: string, newValue: string | null): void {
  window.dispatchEvent(new StorageEvent("storage", { key, newValue }));
}
